import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReduxSelector } from "@hooks/useReduxSelector";
import { useAccessFolderEnterprise } from "@hooks/employeFolder";
import { EmployerCourse } from "@services/all.services";
import { mapEmplacementToDevise } from "@helpers/_functions";
import COUNTRY_LIST from "@pages/localStorage/devise";
import {
  JOBTYPE_LIST,
  LANGUAGE_LIST
} from "@pages/shared/employeFormNew/employeForm-constants";
import useGenderType from "@pages/shared/employeFormNew/PersonnalInformations/hooksGenderType";
import useClothes from "@pages/shared/employeFormNew/PersonnalInformations/hooksClothes";
import { SECTION_PERMISSIONS } from "../utils/bulkEditHelpers";

const idOf = (option) => option._id;
const idsOf = (options) => options.map((option) => option._id || option);
const salaryField =
  (path, toValue = (value) => value) =>
  (employee, value, write) => {
    (employee.salaryAndAdvantages || []).forEach((salary, index) => {
      write(`salaryAndAdvantages.${index}.${path}`, toValue(value));
    });
  };

export default function useBulkInjectionFields({
  isOpen,
  employees,
  isSectionDisplayed,
  hiddenColumns,
  maxAdvantageTypes,
  isSaving,
  hasBankingAccess
}) {
  const { t: jobT, i18n } = useTranslation("pages.employés.emploi&Salary");
  const { t: personalT } = useTranslation("pages.employés.infoPers");
  const { t: healthT } = useTranslation("pages.employés.healthPrecision");
  const { t: countryT } = useTranslation("components.dropdown.dropdownCountry");
  const { t: languageT } = useTranslation("pages.employés.statistics");
  const { isReadOnlySection } = useAccessFolderEnterprise();
  const common = useReduxSelector(({ commonData }) => commonData);
  const settings = useReduxSelector(({ settings }) => settings);
  const options = useReduxSelector(
    ({ enterpriseEmployeData }) => enterpriseEmployeData
  );
  const { allGenderTypes } = useGenderType();
  const { TYPES } = useClothes();
  const [careers, setCareers] = useState([]);
  const [careerLoading, setCareerLoading] = useState(false);
  const selectedEmployees = employees.filter((employee) => employee.selected);
  const salaryVisible = isSectionDisplayed("salaryAndAdvantages");
  const selectedIdsKey = JSON.stringify(
    employees
      .filter((employee) => employee.selected)
      .map((employee) => employee._id || employee.id)
      .filter(Boolean)
  );

  useEffect(() => {
    if (!isOpen || !salaryVisible) return;
    let cancelled = false;
    setCareers([]);
    setCareerLoading(true);
    Promise.all(
      JSON.parse(selectedIdsKey).map(async (employeeId) => {
        try {
          const response = await EmployerCourse.getByEmployer(employeeId);
          return (response?.data?.data?.data || []).map((career) => ({
            ...career,
            employeeId
          }));
        } catch {
          return [];
        }
      })
    ).then((lists) => {
      if (!cancelled) {
        setCareers(lists.flat());
        setCareerLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, salaryVisible, selectedIdsKey]);

  const currencies = mapEmplacementToDevise(common.emplacements || []);
  const fields = [];
  const add = (section, id, label, list, path, extra = {}) =>
    fields.push({
      section,
      id,
      column: id,
      label,
      options: list || [],
      path,
      ...extra
    });

  add("job", "poste", jobT("Poste"), common.postes, "emploiSalary.Poste", {
    column: "position",
    textAttribute: "title"
  });
  const canChangeHierarchical = (employee) => {
    const hierarchicalId = employee?.emploiSalary?.job?.hierarchical?.id;
    return Boolean(
      hierarchicalId &&
        (common.employers || []).some((employer) => employer.id === hierarchicalId)
    );
  };
  add(
    "job",
    "valueHierarchicalSelected",
    jobT("Supérieur hiérarchique"),
    common.employers,
    "emploiSalary.job.hierarchical",
    {
      column: "hierarchical",
      component: "hierarchical",
      canApply: canChangeHierarchical
    }
  );
  add(
    "job",
    "emplacement",
    jobT("Emplacement"),
    common.emplacements,
    "emploiSalary.job.emplacement",
    { column: "location", multiple: true }
  );
  add("job", "groupes", jobT("Groupe"), common.groupes, "groupIds", {
    column: "group",
    component: "group",
    multiple: true,
    toValue: idsOf
  });
  const canChangeSecurity = (employee) =>
    !(common.employers || []).some(
      (employer) =>
        employer.id === (employee._id || employee.id) && employer.lastAdmin
    );
  add(
    "security",
    "authorization",
    jobT("Permission"),
    common.permissions,
    "emploiSalary.Permission",
    {
      column: "permission",
      textAttribute: "role",
      canApply: canChangeSecurity
    }
  );
  add(
    "security",
    "accessProfile",
    jobT("Profil d'accès"),
    settings.accessProfiles?.list,
    "accessProfile",
    { textAttribute: "title", canApply: canChangeSecurity }
  );
  add(
    "personalInfos",
    "legalGender",
    personalT("Genre légal"),
    allGenderTypes,
    "personnalInformation.legalGender",
    { toValue: idOf, withTooltip: true }
  );
  add(
    "personalInfos",
    "originCountry",
    personalT("Pays d'origine"),
    COUNTRY_LIST,
    "personnalInformation.originCountry",
    {
      textAttribute: "pays",
      valueAttribute: "pays",
      toValue: (value) => value.pays,
      translator: countryT
    }
  );
  add(
    "personalInfos",
    "spokenLanguages",
    personalT("Langues parlées"),
    LANGUAGE_LIST,
    "personnalInformation.spokenLanguages",
    { multiple: true, toValue: idsOf, translator: languageT }
  );
  add(
    "healthAllergiesFoodPrefs",
    "allergies",
    healthT("Allergies"),
    options.allergies?.list,
    "allergies",
    { multiple: true }
  );
  add(
    "healthAllergiesFoodPrefs",
    "foodPreferences",
    healthT("Préférences alimentaires"),
    options.foods?.list,
    "foodPreferences",
    { multiple: true }
  );
  TYPES.forEach((type) =>
    add(
      "clothingSize",
      type._id,
      personalT(type.label),
      options.clothes?.list?.filter((clothe) => clothe.type === type._id),
      type._id,
      { multiple: true }
    )
  );
  add(
    "bankingAndInsuranceInformation",
    "insurancePlanNumber",
    jobT("Numéro de plan d'assurance"),
    settings.insuranceAndBanqOptions?.insurancePlanNumberList,
    "emploiSalary.BankingAndInsuranceInformation.insurancePlanNumber",
    { toValue: (value) => value.name }
  );
  add(
    "bankingAndInsuranceInformation",
    "typeCover",
    jobT("Type de couverture"),
    settings.insuranceAndBanqOptions?.coverageTypeList,
    "emploiSalary.BankingAndInsuranceInformation.typeCover",
    { toValue: (value) => value.name }
  );
  (settings.additionalInformation?.list || []).forEach((item) =>
    add(
      "additionalInformation",
      `additionalInfo-${item._id}`,
      i18n.language === "fr" ? item.designationFr : item.designationEn,
      item.infosList,
      `additionalInfo.${item._id}`,
      { column: item._id, toValue: idOf }
    )
  );

  const canChangeCareer = (employee) =>
    (employee.salaryAndAdvantages || []).some(
      (salary) => !salary.emploiCourses?._id
    );

  // Career IDs belong to one employee; never assign another employee's career.
  const careerOptions = careers.map((career) => {
    const employee = employees.find(
      (item) => (item._id || item.id) === career.employeeId
    );
    const person = employee?.personnalInformation;
    const employeeName =
      [person?.legalFirstName, person?.legalName].filter(Boolean).join(" ") ||
      employee?.name ||
      career.employeeId;
    return { ...career, label: `${career.title} (${employeeName})` };
  });
  add(
    "salaryAndAdvantages",
    "emploiCourses",
    jobT("Poste"),
    careerOptions,
    null,
    {
      textAttribute: "label",
      disabled: careerLoading,
      canApply: canChangeCareer,
      apply: (employee, value, write) => {
        if ((employee._id || employee.id) !== value.employeeId) return;
        (employee.salaryAndAdvantages || []).forEach((salary, index) => {
          // The table locks the career once it is assigned.
          if (!salary.emploiCourses?._id)
            write(`salaryAndAdvantages.${index}.emploiCourses._id`, value._id);
        });
      }
    }
  );
  add(
    "salaryAndAdvantages",
    "jobType",
    jobT("Type d'emploi"),
    JOBTYPE_LIST,
    null,
    {
      valueAttribute: "value",
      translator: jobT,
      apply: salaryField("jobType", (value) => value.value)
    }
  );
  add(
    "salaryAndAdvantages",
    "devise",
    `${jobT("Salaire annuel")} — ${jobT("Devise")}`,
    currencies,
    null,
    { column: "salary", apply: salaryField("devise") }
  );
  add(
    "salaryAndAdvantages",
    "hourlyRateDevise",
    `${jobT("Taux horaire")} — ${jobT("Devise")}`,
    currencies,
    null,
    { column: "hourlyRate", apply: salaryField("hourlyRateDevise") }
  );
  for (let index = 0; index < maxAdvantageTypes; index++) {
    const applyAdvantage = (convert) => (employee, value, write) => {
      (employee.salaryAndAdvantages || []).forEach((salary, salaryIndex) => {
        const advantage = salary.AdvantageType?.[index];
        if (advantage)
          write(
            `salaryAndAdvantages.${salaryIndex}.AdvantageType.${index}`,
            convert(advantage, value)
          );
      });
    };
    add(
      "salaryAndAdvantages",
      `advantageType-${index}`,
      `${jobT("Type d'avantage")} ${index + 1}`,
      options.advantageType?.list,
      null,
      {
        apply: applyAdvantage((previous, value) => ({
          ...previous,
          _id: value._id,
          name: value.name
        }))
      }
    );
    add(
      "salaryAndAdvantages",
      `advantageDevise-${index}`,
      `${jobT("Valeur annuelle")} ${index + 1} — ${jobT("Devise")}`,
      currencies,
      null,
      {
        column: `advantageValue-${index}`,
        apply: applyAdvantage((previous, value) => ({
          ...previous,
          annualDevise: value
        }))
      }
    );
  }

  return fields
    .filter(
      (field) =>
        isSectionDisplayed(field.section) && !hiddenColumns[field.column]
    )
    .map((field) => ({
      ...field,
      disabled: Boolean(
        field.disabled ||
        isSaving ||
        (field.canApply &&
          selectedEmployees.length > 0 &&
          !selectedEmployees.some(field.canApply)) ||
        isReadOnlySection(SECTION_PERMISSIONS[field.section]) ||
        (field.section === "bankingAndInsuranceInformation" &&
          !hasBankingAccess)
      )
    }));
}
